// Pure helpers for the Formant-staff robot onboarding plans dashboard:
// human labels, tones, compact formatting and the chart math (window start,
// y-axis scale, stacked bar segments, pie arcs). No React here so the math is
// testable.
import {
  RobotApprovedPlanStatusSchema,
  RobotPlanDeviceDimensionSchema,
  RobotPlanFailureCategorySchema,
  type AdminRobotPlanInsights,
  type RobotApprovedPlanStatus,
  type RobotDeviceCategory,
  type RobotPlanDeviceDimension,
  type RobotPlanDeviceSlice,
  type RobotPlanFailureCategory,
  type RobotPlanRecipeSource,
} from '@cortex/types';

export type RangeKey = '7d' | '30d' | '90d';
export const rangeDays: Record<RangeKey, number> = { '7d': 7, '30d': 30, '90d': 90 };
export const rangeLabels: Record<RangeKey, string> = {
  '7d': 'Last 7d',
  '30d': 'Last 30d',
  '90d': 'Last 90d',
};

/** URL state of the overview page; defaults are omitted to keep links short. */
export interface IRobotPlansSearch {
  range?: RangeKey;
  org?: string;
  status?: RobotApprovedPlanStatus;
  q?: string;
  /** Client-side failure-cause filter of the plans table. */
  cause?: RobotPlanFailureCategory;
}

export function parseRobotPlansSearch(raw: Record<string, unknown>): IRobotPlansSearch {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const range = str(raw.range);
  const status = RobotApprovedPlanStatusSchema.safeParse(raw.status);
  const cause = RobotPlanFailureCategorySchema.safeParse(raw.cause);
  return {
    range: range === '7d' || range === '90d' ? range : undefined,
    org: str(raw.org),
    status: status.success ? status.data : undefined,
    q: str(raw.q),
    cause: cause.success && cause.data !== 'none' ? cause.data : undefined,
  };
}

/** URL state of the Devices page; defaults are omitted to keep links short. */
export interface IRobotPlansDevicesSearch {
  range?: RangeKey;
  org?: string;
  /** Dimension the pie and totals table slice by; default `deviceType`. */
  dim?: RobotPlanDeviceDimension;
}

export function parseRobotPlansDevicesSearch(
  raw: Record<string, unknown>,
): IRobotPlansDevicesSearch {
  const { range, org } = parseRobotPlansSearch(raw);
  const dim = RobotPlanDeviceDimensionSchema.safeParse(raw.dim);
  return {
    range,
    org,
    dim: dim.success && dim.data !== 'deviceType' ? dim.data : undefined,
  };
}

/**
 * Window start for a range: UTC midnight `days - 1` days before `now`, so the
 * window is exactly `days` whole UTC days including today. Stable for a whole
 * day, which keeps the query key (and the cache) stable across renders.
 */
export function rangeSince(range: RangeKey, now: Date): string {
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(start - (rangeDays[range] - 1) * 86_400_000).toISOString();
}

/** "Oct 13" for a UTC day (`YYYY-MM-DD` or ISO), never shifted by the local zone. */
export function formatDay(date: string, withWeekday = false): string {
  const d = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    ...(withWeekday ? { weekday: 'short' } : {}),
  });
}

export const statusLabels: Record<RobotApprovedPlanStatus, string> = {
  approved: 'Approved',
  applied: 'Applying',
  live: 'Live',
  failed: 'Failed',
  superseded: 'Superseded',
};

export const statusBadgeClass: Record<RobotApprovedPlanStatus, string> = {
  approved: 'badge badge--outline',
  applied: 'badge badge--info',
  live: 'badge badge--success',
  failed: 'badge badge--danger',
  superseded: 'badge badge--neutral',
};

/** Solid color per status — the same palette the outcome chart stacks with. */
export const statusColor: Record<RobotApprovedPlanStatus, string> = {
  approved: 'var(--status-info)',
  applied: 'var(--status-info)',
  live: 'var(--status-success)',
  failed: 'var(--status-danger)',
  superseded: 'var(--status-neutral)',
};

export const failureLabels: Record<RobotPlanFailureCategory, { label: string; hint: string }> = {
  apply_interrupted: {
    label: 'Apply interrupted',
    hint: 'The robot agent restarted while the plan was being applied.',
  },
  connector_failed: {
    label: 'Connector failed',
    hint: 'Installing or starting the robot connector failed.',
  },
  realtime_peer_down: {
    label: 'Realtime peer never came up',
    hint: 'The realtime peer wrote no status after apply started.',
  },
  twist_not_received: {
    label: 'Teleop twist not received',
    hint: 'Teleop commands never reached the robot driver.',
  },
  robot_did_not_move: {
    label: "Robot didn't move",
    hint: 'The drive test ran but the robot did not move.',
  },
  camera_failed: { label: 'Camera failed', hint: 'A camera stream could not be set up.' },
  streams_silent: {
    label: 'Streams silent',
    hint: 'Telemetry streams were configured but never sent data.',
  },
  device_name_conflict: {
    label: 'Device name conflict',
    hint: 'A Formant device with that name already exists.',
  },
  no_receipt: { label: 'No receipt', hint: 'The plan failed without reporting a receipt.' },
  other: { label: 'Other error', hint: 'An error that matched no known cause.' },
  none: { label: '—', hint: '' },
};

/** Outcome series of the daily chart, bottom → top. */
export const outcomeSeries = ['live', 'failed', 'superseded', 'inFlight'] as const;
export type OutcomeKey = (typeof outcomeSeries)[number];
export const outcomeLabels: Record<OutcomeKey, string> = {
  live: 'Live',
  failed: 'Failed',
  superseded: 'Superseded',
  inFlight: 'In flight',
};
export const outcomeColors: Record<OutcomeKey, string> = {
  live: statusColor.live,
  failed: statusColor.failed,
  superseded: statusColor.superseded,
  inFlight: statusColor.applied,
};

/** "73%" — whole percent of a 0..1 ratio; null/NaN → dash. */
export function formatRate(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** Share `part / whole` as a whole percent; zero whole → dash. */
export function formatShare(part: number, whole: number): string {
  return whole > 0 ? formatRate(part / whole) : '—';
}

/** "$12.34" / "$0.004" / "$0" — cent precision once a dollar is reached. */
export function formatCost(usd: number | null | undefined): string {
  if (usd === null || usd === undefined || !Number.isFinite(usd)) return '—';
  if (usd === 0) return '$0';
  if (Math.abs(usd) >= 1) return `$${usd.toFixed(2)}`;
  return `$${usd.toPrecision(2)}`;
}

/** Display name of a robot: its name, else its source id. */
export function robotLabel(r: { robotName: string | null; robotSourceId: string }): string {
  return r.robotName?.trim() || r.robotSourceId;
}

/**
 * Smallest "nice" axis maximum (1, 2, 5 × 10^n) at or above `value`, never
 * below 1, so integer-count charts get clean gridlines.
 */
export function niceCeil(value: number): number {
  if (!Number.isFinite(value) || value <= 1) return 1;
  const pow = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (step * pow >= value) return step * pow;
  }
  return 10 * pow;
}

export interface IStackSegment {
  key: OutcomeKey;
  value: number;
  /** Offset from the baseline, in the same units as `height`. */
  y0: number;
  height: number;
}

/**
 * Stacks one day's outcomes bottom → top (live first) into segments scaled so
 * `max` maps to `plotHeight`. Zero-valued series produce no segment.
 */
export function stackDay(
  day: AdminRobotPlanInsights['daily'][number],
  max: number,
  plotHeight: number,
): IStackSegment[] {
  const out: IStackSegment[] = [];
  if (max <= 0) return out;
  let y0 = 0;
  for (const key of outcomeSeries) {
    const value = day[key];
    if (value <= 0) continue;
    const height = (value / max) * plotHeight;
    out.push({ key, value, y0, height });
    y0 += height;
  }
  return out;
}

/** Total plans approved on one day of the outcome chart. */
export function dayTotal(day: AdminRobotPlanInsights['daily'][number]): number {
  return outcomeSeries.reduce((sum, key) => sum + day[key], 0);
}

/** A receipt value as one readable line: strings verbatim, else compact JSON. */
export function receiptLine(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** "14:03:22" in the viewer's zone, for timelines inside one run. */
const clockFmt = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});
export function formatClock(iso: string): string {
  return clockFmt.format(new Date(iso));
}

// ── Devices ──────────────────────────────────────────────────────────────

export const deviceDimensions = RobotPlanDeviceDimensionSchema.options;
export const deviceDimensionLabels: Record<RobotPlanDeviceDimension, string> = {
  deviceType: 'Device type',
  deviceCategory: 'Category',
  os: 'OS',
  arch: 'Arch',
};

/** The same dimensions mid-sentence ("Onboardings by OS", "Per device type"). */
export const deviceDimensionNouns: Record<RobotPlanDeviceDimension, string> = {
  deviceType: 'device type',
  deviceCategory: 'category',
  os: 'OS',
  arch: 'architecture',
};

export const deviceCategoryLabels: Record<RobotDeviceCategory, string> = {
  quadruped: 'Quadruped',
  wheeled: 'Wheeled',
  arm: 'Arm',
  drone: 'Drone',
  humanoid: 'Humanoid',
  computer: 'Computer',
  other: 'Other',
};

/** Display label of a slice key; categories get their human label. */
export function sliceLabel(dim: RobotPlanDeviceDimension, key: string): string {
  if (dim === 'deviceCategory' && key in deviceCategoryLabels) {
    return deviceCategoryLabels[key as RobotDeviceCategory];
  }
  return key;
}

/** True for a missing / placeholder device fact (`null`, empty, `unknown`). */
export function isUnknownFact(value: string | null | undefined): boolean {
  return !value?.trim() || value.trim().toLowerCase() === 'unknown';
}

/** "Ubuntu 22.04" — OS name plus version; null when the OS is unknown. */
export function osLabel(os: string | null | undefined, osVersion?: string | null): string | null {
  if (isUnknownFact(os)) return null;
  const version = isUnknownFact(osVersion) ? '' : ` ${osVersion?.trim()}`;
  return `${os?.trim()}${version}`;
}

/** "Ubuntu 22.04 · aarch64", either half alone, or "Unknown" when neither is known. */
export function osArchLabel(r: {
  os: string | null;
  osVersion?: string | null;
  arch: string | null;
}): string {
  const parts = [osLabel(r.os, r.osVersion), isUnknownFact(r.arch) ? null : r.arch?.trim()];
  const known = parts.filter((p): p is string => Boolean(p));
  return known.length > 0 ? known.join(' · ') : 'Unknown';
}

/** Live share of settled plans, live / (live + failed); null when none settled. */
export function liveRate(s: { live: number; failed: number }): number | null {
  const settled = s.live + s.failed;
  return settled > 0 ? s.live / settled : null;
}

/**
 * What a failed plan failed at: the apply step when the receipt names one,
 * else the failure cause's label.
 */
export function failingStep(
  step: string | null,
  category: RobotPlanFailureCategory,
): { text: string; isStep: boolean } {
  if (step?.trim()) return { text: step.trim(), isStep: true };
  return { text: failureLabels[category].label, isStep: false };
}

/** A slice of the devices pie: one dimension value, or the merged tail. */
export interface IPieSlice extends RobotPlanDeviceSlice {
  /** How many keys were merged into this slice; 0 for a real key. */
  merged: number;
}

export const pieOtherKey = 'Other';

/**
 * The `max` biggest slices (input is most-plans-first), plus one "Other"
 * slice summing the rest when there are more. Robot counts of the merged
 * slice are summed, so a robot seen under two merged keys counts twice.
 */
export function groupTopSlices(slices: RobotPlanDeviceSlice[], max = 8): IPieSlice[] {
  const sorted = [...slices].sort((a, b) => b.plans - a.plans);
  if (sorted.length <= max) return sorted.map((s) => ({ ...s, merged: 0 }));
  const head = sorted.slice(0, max).map((s) => ({ ...s, merged: 0 }));
  const tail = sorted.slice(max);
  const other: IPieSlice = {
    key: pieOtherKey,
    plans: 0,
    robots: 0,
    live: 0,
    failed: 0,
    inFlight: 0,
    superseded: 0,
    robotsLive: 0,
    merged: tail.length,
  };
  for (const s of tail) {
    other.plans += s.plans;
    other.robots += s.robots;
    other.live += s.live;
    other.failed += s.failed;
    other.inFlight += s.inFlight;
    other.superseded += s.superseded;
    other.robotsLive += s.robotsLive;
  }
  return [...head, other];
}

/** Pie slice colors in slice order; "Other" and "Unknown" are always grey. */
export const pieColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
];
export const pieOtherColor = 'var(--status-neutral)';
export const pieUnknownColor = 'var(--text-muted)';

export function pieColor(slice: IPieSlice, index: number): string {
  if (slice.merged > 0) return pieOtherColor;
  if (isUnknownFact(slice.key)) return pieUnknownColor;
  return pieColors[index % pieColors.length] ?? pieOtherColor;
}

export interface IPieAngle {
  /** Radians clockwise from 12 o'clock. */
  start: number;
  end: number;
  /** 0..1 of the total. */
  share: number;
}

/** Consecutive angles for `values` around a full circle; zero total → no slices. */
export function pieAngles(values: number[]): IPieAngle[] {
  const total = values.reduce((sum, v) => sum + Math.max(0, v), 0);
  if (total <= 0) return [];
  let start = 0;
  return values.map((v) => {
    const share = Math.max(0, v) / total;
    const end = start + share * Math.PI * 2;
    const out = { start, end, share };
    start = end;
    return out;
  });
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

function polar(cx: number, cy: number, r: number, angle: number): string {
  return `${round3(cx + r * Math.sin(angle))} ${round3(cy - r * Math.cos(angle))}`;
}

/**
 * SVG path of a pie wedge (`inner` 0) or donut segment between two angles.
 * A full turn is drawn as two half arcs (an arc to its own start point draws
 * nothing); pair it with `fill-rule="evenodd"` for a donut ring.
 */
export function arcPath(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  start: number,
  end: number,
): string {
  const sweep = end - start;
  if (sweep <= 0) return '';
  if (sweep >= Math.PI * 2 - 1e-6) {
    const ring = (r: number, dir: 0 | 1) =>
      `M ${polar(cx, cy, r, 0)} A ${r} ${r} 0 1 ${dir} ${polar(cx, cy, r, Math.PI)} ` +
      `A ${r} ${r} 0 1 ${dir} ${polar(cx, cy, r, 0)} Z`;
    return inner > 0 ? `${ring(outer, 1)} ${ring(inner, 0)}` : ring(outer, 1);
  }
  const large = sweep > Math.PI ? 1 : 0;
  const outerArc = `M ${polar(cx, cy, outer, start)} A ${outer} ${outer} 0 ${large} 1 ${polar(cx, cy, outer, end)}`;
  if (inner <= 0) return `${outerArc} L ${round3(cx)} ${round3(cy)} Z`;
  return (
    `${outerArc} L ${polar(cx, cy, inner, end)} ` +
    `A ${inner} ${inner} 0 ${large} 0 ${polar(cx, cy, inner, start)} Z`
  );
}

// ── Recipe ("How it was made to work") ───────────────────────────────────

export const recipeSourceLabels: Record<RobotPlanRecipeSource, string> = {
  plan: 'plan',
  approval: 'approval card',
  tool_call: 'tool call',
};

/** Monaco language id for a fenced-code language. */
export function monacoLanguage(language: string): string {
  const lang = language.trim().toLowerCase();
  if (['bash', 'sh', 'shell', 'zsh', 'console'].includes(lang)) return 'shell';
  if (['python', 'py', 'python3'].includes(lang)) return 'python';
  if (['js', 'javascript', 'node'].includes(lang)) return 'javascript';
  if (['ts', 'typescript'].includes(lang)) return 'typescript';
  if (['yaml', 'yml'].includes(lang)) return 'yaml';
  if (lang === 'json') return 'json';
  return 'plaintext';
}

export function lineCount(code: string): number {
  return code.replace(/\n$/, '').split('\n').length;
}

/** Scripts longer than this start collapsed. */
export const scriptCollapseLines = 25;

/** Read-only code viewer height for `code`: Monaco's 18 px line (font size 12) plus padding, capped. */
export function codeViewerHeight(code: string, cap = 520): number {
  return Math.min(lineCount(code) * 18 + 8, cap);
}

/** Robot tools that only look (explore, preview, wait) rather than change the robot. */
const readOnlyRobotTools: Record<string, true> = {
  run: true,
  preview: true,
  discover: true,
  status: true,
  wait: true,
  health: true,
};

/** A `robot:<tool>` call that only reads the robot. */
export function isReadOnlyRobotCall(tool: string): boolean {
  return tool.startsWith('robot:') && readOnlyRobotTools[tool.slice('robot:'.length)] === true;
}

/**
 * Splits an apply step's extra args into scripts (multi-line strings, or any
 * string under a `script`/`code` key) to show as code, and the rest.
 */
export function splitStepArgs(args: Record<string, unknown>): {
  scripts: Array<{ key: string; code: string }>;
  rest: Array<[string, unknown]>;
} {
  const scripts: Array<{ key: string; code: string }> = [];
  const rest: Array<[string, unknown]> = [];
  for (const [key, value] of Object.entries(args)) {
    if (typeof value === 'string' && (value.includes('\n') || /script|code/i.test(key))) {
      scripts.push({ key, code: value });
    } else {
      rest.push([key, value]);
    }
  }
  return { scripts, rest };
}

/**
 * Language of an unfenced step script from its first line: a shebang decides,
 * else a leading python import means python; anything else is bash.
 */
export function guessScriptLanguage(code: string): 'python' | 'bash' {
  const first = code.trimStart().split('\n', 1)[0] ?? '';
  if (first.startsWith('#!')) return first.includes('python') ? 'python' : 'bash';
  return /^(import \w|from \S+ import )/.test(first) ? 'python' : 'bash';
}

/** The standard onboard-agent install line; relay URL and token are per robot and not stored. */
export const onboardAgentInstallCommand =
  'curl -fsSL https://github.com/FormantIO/onboard-agent-releases/releases/latest/download/install.sh' +
  ' | sudo bash -s -- --relay <relay-url> --token <robot-token>';
